import React, { useState } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/bthTriangle.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import { format, parse } from "date-fns";

const CourierFilterModal = ({
  show,
  handleClose,
  setCustomerData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
  defaultFilterData,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: filterData,
    mode: "onChange",
  });

  const [selectedTab, setSelectedTab] = useState("Company ID");
  const [loading, setLoading] = useState(false);
  const loggedInUserName = sessionStorage.getItem("username");

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const onSubmit = () => {
    fetchCustomerData(currentPage);
  };

  const fetchCustomerData = async (currentPage) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        ...filterData,
        fromDate: formatDate(filterData.fromDate),
        toDate: formatDate(filterData.toDate),
        status: filterData.status.length > 0 ? filterData.status.map(s => s.toUpperCase()) : [],
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_COURIER_MASTER_DATA}?pageSize=${pageSize}&pageNo=${1}`,
        requestBody
      );

      if (response?.data?.status === true && response?.data?.respCode === "00") {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setCustomerData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setCustomerData([]);
        setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((user, index) => {
      const formattedCreDate = user?.createdDate
        ? parse(user.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;
      const addressComponents = [
        user.address1,
        user.address2,
        user.address3,
        user.pincode,
        user.city,
        user.state,
        user.country,
      ].filter((component) => component != null && component !== "");
      const address =
        addressComponents.length > 0 ? addressComponents.join(", ") : "";

      return {
        ...user,
        address,
        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",
      };
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const statusOptions = [
    "Approved",
    "In-Progress",
    "Blocked",
    "Pending",
    "Rejected",
  ];

  const onReset = () => {
    handleReset();
    reset(defaultFilterData); 
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                }}
              >
                <h5 className="modal-title main-title">Filter</h5>
                <HighlightOffIcon
                  onClick={handleClose}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {[
                        "Company ID",
                        "Retailer Code",
                        "Pincode",
                        "Created Date",
                        "Status",
                      ].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content">
                      {selectedTab === "Company ID" && (
                        <div className="dateField">
                          <div className="dateInputWrapper">
                            <label className="dateLabel">Company ID</label>
                            <input
                              type="text"
                              placeholder="Enter Company ID"
                              className="dateInput"
                              value={filterData?.companyId}
                              maxLength={20}
                              pattern="[A-Za-z0-9]+"
                              onChange={(e) => {
                                const val = e.target.value;
                                const cleanedVal = val.replace(
                                  /[^a-zA-Z0-9]/g,
                                  ""
                                );
                                setFilterData((prev) => ({
                                  ...prev,
                                  companyId: cleanedVal,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedTab === "Retailer Code" && (
                        <div className="dateField">
                          <div className="dateInputWrapper">
                            <label className="dateLabel">Retailer Code</label>
                            <input
                              type="text"
                              placeholder="Enter Retailer Code"
                              className="dateInput"
                              value={filterData?.retailerCode}
                              maxLength={20}
                              pattern="[A-Za-z0-9]+"
                              onChange={(e) => {
                                const val = e.target.value;
                                const cleanedVal = val.replace(
                                  /[^a-zA-Z0-9]/g,
                                  ""
                                );
                                setFilterData((prev) => ({
                                  ...prev,
                                  retailerCode: cleanedVal,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedTab === "Pincode" && (
                        <div className="dateField">
                          <div className="dateInputWrapper">
                            <label className="dateLabel">Pincode</label>
                            <input
                              type="text"
                              placeholder="Enter Pincode"
                              className="dateInput"
                              value={filterData?.pincode}
                              maxLength={6}
                              pattern="[0-9]{6}"
                              onChange={(e) => {
                                const val = e.target.value;
                                const cleanedVal = val.replace(/[^0-9]/g, "");
                                setFilterData((prev) => ({
                                  ...prev,
                                  pincode: cleanedVal,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value) =>
                                  !value ||
                                  !filterData.toDate ||
                                  new Date(value) <=
                                    new Date(filterData.toDate) ||
                                  "From Date must be before To Date",
                                  requiredIfToDate: (value, filterData) =>
                                  !filterData.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <span className="error-text">
                                      {errors.fromDate.message}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value) =>
                                  !value ||
                                  !filterData.fromDate ||
                                  new Date(value) >=
                                    new Date(filterData.fromDate) ||
                                  "To Date must be after From Date",
                                  requiredIfFromDate: (value, filterData) =>
                                  !filterData.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.toDate && (
                                    <span className="error-text">
                                      {errors.toDate.message}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Status"? (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "25px",
                            }}
                          >
                            Status
                          </label>
                          <div
                            className="dateInputWrapper"
                            style={{
                              display: "flex",
                              gap: "1rem",
                              flexWrap: "wrap",
                              padding: "0.75rem",
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #ced4da",
                              borderRadius: "0.5rem",
                            }}
                          >
                            {statusOptions.map((status) => (
                              <label
                                key={status}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer",
                                  fontSize: "0.9rem",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  value={status}
                                  checked={filterData.status.includes(status)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setFilterData((prev) => ({
                                      ...prev,
                                      status: prev.status.includes(value)
                                        ? prev.status.filter((s) => s !== value)
                                        : [...prev.status, value],
                                    }));
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {status}
                              </label>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CourierFilterModal;